// Lengte van een bill-shock-periode, in hele maanden.
//
// De tabel toont de periode als "van → tot". De kolom Months zet daar één getal
// naast, zodat je in één oogopslag ziet of een afwijking over een volledig jaar
// loopt of over twee maanden — een bill shock van € 300 betekent iets anders bij
// 12 maanden dan bij 2.
//
// REKENREGEL: volle kalendermaanden + de resterende dagen als fractie van de
// maand waarin ze vallen, daarna wiskundig afgerond (0,5 gaat omhoog). Bewust
// kalendermaanden en geen "dagen / 30,44": zo levert 01/01 → 01/02 exact 1 op en
// 01/05/2026 → 01/05/2027 exact 12, ongeacht februari of een schrikkeljaar. Dat
// is wat iemand die de datums naleest zelf ook uitrekent.
//
// De grens loopt op de DAG, niet op het uur: Lynx levert periodes die op lokale
// middernacht beginnen en dus als 22:00Z of 23:00Z in de data staan (winter- vs
// zomertijd). Op uurniveau rekenen zou 30/04 22:00Z → 28/02 23:00Z een uur laten
// missen en bij een grensgeval een maand schelen.

const dagDelen = (iso) => {
  const d = new Date(iso);
  if (isNaN(d)) return null;
  // Lokale Belgische dag: 30/04 22:00Z is hier 01/05. Zonder die omzetting valt
  // een periode die om middernacht start een dag te vroeg.
  const s = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Brussels', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  const [j, m, dg] = s.split('-').map(Number);
  return { jaar: j, maand: m, dag: dg };
};

// Dagen sinds een vast nulpunt — genoeg om verschillen in dagen te berekenen
// zonder tijdzone- of zomertijdgedoe.
const alsDagnummer = ({ jaar, maand, dag }) => Math.round(Date.UTC(jaar, maand - 1, dag) / 86400000);

// Aantal dagen in de maand die op (jaar, maand) volgt vanaf `dag` — de noemer van
// de restfractie.
const maandLengte = (jaar, maand, dag) =>
  Math.round((Date.UTC(jaar, maand, dag) - Date.UTC(jaar, maand - 1, dag)) / 86400000);

// De kalendermaand (Belgische tijd) waarin een tijdstip valt, als 'JJJJ-MM'.
// Dezelfde dag-afspraak als hierboven: 31/10 23:00Z is hier 01/11, dus november.
// Voedt de grafiek "Afrekeningen per maand" (einde van de periode = de afrekening).
export function maandVanDatum(iso) {
  if (!iso) return null;          // new Date(null) is 1970 — géén maand
  const d = dagDelen(iso);
  return d ? `${d.jaar}-${String(d.maand).padStart(2, '0')}` : null;
}

export function maandenInPeriode(fromUtc, toUtc) {
  if (!fromUtc || !toUtc) return null;
  const a = dagDelen(fromUtc);
  const b = dagDelen(toUtc);
  if (!a || !b) return null;

  const eind = alsDagnummer(b);
  let maanden = (b.jaar - a.jaar) * 12 + (b.maand - a.maand);
  if (b.dag < a.dag) maanden -= 1;                 // de laatste maand is nog niet vol
  if (maanden < 0) return 0;                        // tot vóór van → geen periode

  // Waar staan we na `maanden` volle maanden? Een startdag die niet in de
  // doelmaand bestaat (31 → februari) rolt door naar de eerstvolgende dag; dat is
  // dezelfde afspraak die een mens maakt en het scheelt hooguit een fractie.
  const ankerJaar = a.jaar + Math.floor((a.maand - 1 + maanden) / 12);
  const ankerMaand = ((a.maand - 1 + maanden) % 12) + 1;
  const anker = alsDagnummer({ jaar: ankerJaar, maand: ankerMaand, dag: a.dag });
  const noemer = maandLengte(ankerJaar, ankerMaand, a.dag);
  const restdagen = eind - anker;
  const fractie = noemer > 0 ? restdagen / noemer : 0;

  return Math.round(maanden + fractie);
}
